import React from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import agent from "../../../agent";
import ProbeTable from "../CarrierTariffs/ProbeTable";
import {
  toEditorRows,
  emptyRow,
  toLadderPoints,
  validateRows,
  buildLadderSummary,
  mileLabel,
} from "./ladderAdapters";

/**
 * Редактор тарифной лестницы ноги.
 *
 * Редактируются точки лестницы — вес, цена, надбавка, шаг. Границы весовых
 * диапазонов оператору не показываются и им не задаются: они производные,
 * сервер пересобирает их сам. Иначе правка одной строки развалила бы лестницу,
 * а расчёт начал бы молча врать.
 */
const LadderEditor = ({ leg, onClose, onDeleted }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [deletion, setDeletion] = React.useState(null);

  React.useEffect(() => {
    setLoading(true);
    setResult(null);
    setError(null);
    agent.CarrierDeliveryLegs.readLadder(leg.legId)
      .then((ladder) => setRows(toEditorRows(ladder.points)))
      .catch((e) => setError(readError(e, "Не удалось загрузить лестницу")))
      .finally(() => setLoading(false));
  }, [leg.legId]);

  const problems = React.useMemo(() => validateRows(rows), [rows]);
  const summary = buildLadderSummary(result);

  const change = (key, field, value) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  const addRow = () => setRows((current) => [...current, emptyRow()]);
  const removeRow = (key) => setRows((current) => current.filter((row) => row.key !== key));

  const send = (action, label) => {
    setBusy(label);
    setError(null);
    action(leg.legId, { points: toLadderPoints(rows) })
      .then(setResult)
      .catch((e) => setError(readError(e, "Не выполнено")))
      .finally(() => setBusy(null));
  };

  const runDeletion = () => {
    setBusy("delete");
    agent.CarrierDeliveryLegs.remove(leg.legId)
      .then(() => onDeleted(leg))
      .catch((e) => setError(readError(e, "Не удалось удалить ногу")))
      .finally(() => setBusy(null));
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><ProgressSpinner /></div>;
  }

  return (
    <section style={{ borderTop: "2px solid #111827", paddingTop: 16, marginTop: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>
          {leg.psCode} {leg.productCode} · {mileLabel(leg.mileType)} · {leg.originCity} → {leg.destinationCity}
        </h3>
        <Button label="Закрыть" text onClick={onClose} />
      </div>

      {error && <Message severity="error" text={error} style={{ display: "block", margin: "12px 0" }} />}

      <table style={{ borderCollapse: "collapse", marginTop: 12, fontSize: "0.92rem" }}>
        <caption style={{ captionSide: "bottom", textAlign: "left", padding: "8px 0", color: "#6b7280", maxWidth: 640 }}>
          Точка лестницы — вес, при котором действует цена. Надбавка берётся за килограммы
          выше этой точки. Границы весовых диапазонов сервер строит сам.
        </caption>
        <thead>
          <tr>
            <th style={head}>Вес, кг</th>
            <th style={head}>Цена</th>
            <th style={head}>Надбавка за кг</th>
            <th style={head}>Шаг надбавки</th>
            <th style={head} aria-label="Действия" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              {["weight", "price", "priceBreak", "weightBreak"].map((field) => (
                <td key={field} style={cell}>
                  <InputText
                    value={row[field]}
                    aria-label={`${fieldLabels[field]}, строка ${rows.indexOf(row) + 1}`}
                    onChange={(e) => change(row.key, field, e.target.value)}
                    style={{ width: 120, textAlign: "right" }}
                  />
                </td>
              ))}
              <td style={cell}>
                <Button icon="pi pi-trash" text severity="danger"
                        aria-label="Удалить точку" onClick={() => removeRow(row.key)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <Button label="Добавить точку" icon="pi pi-plus" outlined onClick={addRow} />
        <Button
          label="Проверить"
          icon="pi pi-search"
          disabled={problems.length > 0 || busy !== null}
          onClick={() => send(agent.CarrierDeliveryLegs.previewLadder, "preview")}
        />
        <Button
          label="Сохранить"
          icon="pi pi-save"
          severity="success"
          disabled={problems.length > 0 || busy !== null}
          onClick={() => send(agent.CarrierDeliveryLegs.replaceLadder, "save")}
        />
        <Button
          label="Удалить ногу"
          icon="pi pi-trash"
          severity="danger"
          outlined
          disabled={busy !== null}
          onClick={() => agent.CarrierDeliveryLegs.deletionPreview(leg.legId).then(setDeletion)}
        />
      </div>

      {problems.length > 0 && (
        <ul style={{ color: "#b91c1c", marginTop: 12 }}>
          {problems.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      )}

      {busy && <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><ProgressSpinner style={{ width: 40 }} /></div>}

      {deletion && (
        <div style={{ marginTop: 16, padding: 16, border: "1px solid #fecaca", background: "#fef2f2", borderRadius: 8 }}>
          <strong>Удалить ногу целиком?</strong>
          <p style={{ margin: "6px 0" }}>
            Вместе с ней уйдёт вся её лестница — {deletion.ladderPoints} точек.
            Маршрут {deletion.originCity} → {deletion.destinationCity}, {mileLabel(deletion.mileType)}.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Button label="Удалить" severity="danger" onClick={runDeletion} />
            <Button label="Отмена" text onClick={() => setDeletion(null)} />
          </div>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 20 }}>
          <Message
            severity={summary.saved ? "success" : "info"}
            text={summary.saved
              ? `Сохранено: было ${summary.pointsBefore} точек, стало ${summary.pointsAfter}.`
              : `Проверка: лестница даст ${summary.pointsAfter} точек. Ничего не сохранено.`}
            style={{ display: "block", marginBottom: 12 }}
          />

          {summary.rejected.length > 0 && (
            <ul style={{ color: "#92400e" }}>
              {summary.rejected.map((reject) => (
                <li key={`${reject.index}-${reject.reason}`}>
                  строка {reject.index + 1}{reject.weight ? ` (${reject.weight} кг)` : ""}: {reject.reason}
                </li>
              ))}
            </ul>
          )}

          {summary.monotonicityViolations > 0 && (
            <Message
              severity="error"
              text="На части весов цена падает при росте веса. Проверьте точки — при такой лестнице клиент за больший вес заплатит меньше."
              style={{ display: "block", marginBottom: 8 }}
            />
          )}
          {summary.ladderInconsistencies > 0 && (
            <Message
              severity="warn"
              text="Цены точек не сходятся с надбавкой за килограмм. Это противоречие в самом тарифе: решите, править цифры или запросить у перевозчика исправленный файл."
              style={{ display: "block", marginBottom: 8 }}
            />
          )}

          <h4>Контрольные точки</h4>
          <ProbeTable probe={summary.probe} />
        </div>
      )}
    </section>
  );
};

const fieldLabels = {
  weight: "Вес",
  price: "Цена",
  priceBreak: "Надбавка",
  weightBreak: "Шаг надбавки",
};

const head = {
  textAlign: "left",
  padding: "6px 10px",
  borderBottom: "1px solid #d1d5db",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#6b7280",
  whiteSpace: "nowrap",
};

const cell = { padding: "4px 10px", borderBottom: "1px solid #f1f5f9" };

const readError = (e, fallback) =>
  e?.response?.body?.message || e?.response?.body?.error || e?.message || fallback;

export default LadderEditor;
